import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	SquareCheck,
	Repeat,
	MessageSquare,
	MessagesSquare,
	ChevronRight,
	AlertTriangle,
	Check,
	CheckCheck
} from 'lucide-react';
import moment from 'moment';
import T from 'Components/Helpers/translate';
import AssistantResource, { type InboxItem } from 'Resources/AssistantChat';
import { inboxCountStore } from 'App/pages/FloatingAIChat/useInboxCount';
import { notifyAIWorkspaceError } from '../notifyAIWorkspaceError';
import { renderMarkdown } from '../components/markdownRenderer';
import { buildPath } from '../useWorkspaceNav';

type Props = {
	currentConversationId: number | null;
	/** Open the conversation an inbox row belongs to. */
	onSelectConversation: (conversationId: number) => void;
	refreshKey?: number;
};

const ROUTINE_CONTEXTS = new Set(['routine', 'scheduled', 'event_hook']);

/** Strip markdown to a single plain-text line for the row preview. */
const previewText = (md: string | null): string => {
	if (!md) return '';
	return md
		.replace(/```[\s\S]*?```/g, ' ')
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		.replace(/^\s*[-+*]\s+/gm, '')
		.replace(/^\s*\d+\.\s+/gm, '')
		.replace(/[*_`#>]/g, '')
		.replace(/\s+/g, ' ')
		.trim();
};

/** Today → time, this week → weekday, older → day + month. */
const formatWhen = (date: string): string => {
	const m = moment(date);
	const now = moment();
	if (m.isSame(now, 'day')) return m.format('LT');
	if (m.isSame(now, 'week')) return m.format('ddd');
	return m.format('D MMM');
};

type RowDescriptor = {
	tile: 'proposal' | 'routine' | 'message' | 'failed';
	Icon: typeof MessageSquare;
	/** Proposals + failed runs carry a pill; the icon tile conveys routine vs message. */
	tag: string | null;
	title: string;
	sub: string;
};

/**
 * WEB-6799 — the Inbox page. A single compact feed grouped into "Needs you"
 * (pending proposals), "Unread" (unseen agent activity) and "Earlier" (already
 * seen, dimmed). Each row carries a type tag, a one-line preview, an unread dot
 * and a timestamp, and opens its conversation on click.
 */
const WorkspaceInbox = ({ currentConversationId, onSelectConversation, refreshKey }: Props) => {
	const [items, setItems] = useState<InboxItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [expanded, setExpanded] = useState<Set<number>>(new Set());
	const [earlierExpanded, setEarlierExpanded] = useState(false);

	// Monotonic token shared by every load (the effect and the mark handlers).
	// Only the most recent load applies its result; unmount bumps it so a
	// late-resolving getInbox can't setState on an unmounted component or
	// clobber a newer list.
	const loadToken = useRef(0);
	useEffect(() => () => void (loadToken.current += 1), []);

	// `autoExpand` opens the latest unread up front — only wanted on the initial
	// page load, not on the silent refreshes that follow a mark-as-read (those
	// must leave the user's current expand state alone).
	const load = useCallback((opts?: { autoExpand?: boolean }) => {
		const token = (loadToken.current += 1);
		const autoExpand = opts?.autoExpand ?? false;
		const current = () => token === loadToken.current;
		setLoading(true);
		AssistantResource.getInbox()
			.then(rows => {
				if (!current()) return;
				setItems(rows);
				// Keep the floating-chat badge in sync from the rows we just
				// fetched, so a mark-as-read doesn't need a second getInbox call.
				inboxCountStore.setFromItems(rows);
				if (autoExpand) {
					setEarlierExpanded(false);
					const firstUnread = rows.find(i => i.kind === 'unread');
					setExpanded(
						firstUnread && firstUnread.conversationId != null
							? new Set([firstUnread.conversationId])
							: new Set()
					);
				}
			})
			.catch(err => {
				notifyAIWorkspaceError(err, 'getInbox');
				if (current()) setItems([]);
			})
			.finally(() => {
				if (current()) setLoading(false);
			});
	}, []);

	useEffect(() => load({ autoExpand: true }), [refreshKey, load]);

	// Mark handlers reload the feed (which also re-syncs the badge, see load);
	// the marked row leaves "Unread" and moves to "Earlier".
	const markRead = (conversationId: number) =>
		AssistantResource.markInboxRead(conversationId)
			.then(() => load())
			.catch(err => notifyAIWorkspaceError(err, 'markInboxRead'));

	// "Mark all" clears exactly the Unread rows the user currently sees; passing
	// their ids keeps approval-hidden conversations out of the sweep.
	const markAllRead = () => {
		const unreadIds = items
			.filter(i => i.kind === 'unread')
			.map(i => i.conversationId)
			.filter((id): id is number => id != null);
		return AssistantResource.markAllInboxRead(unreadIds)
			.then(() => load())
			.catch(err => notifyAIWorkspaceError(err, 'markAllInboxRead'));
	};

	const toggle = (conversationId: number) =>
		setExpanded(prev => {
			const next = new Set(prev);
			if (next.has(conversationId)) next.delete(conversationId);
			else next.add(conversationId);
			return next;
		});

	const describe = (item: InboxItem): RowDescriptor => {
		if (item.kind === 'approval') {
			return {
				tile: 'proposal',
				Icon: SquareCheck,
				tag: T('assistant.inbox.tag.proposal') || 'Proposal',
				title: item.title || T('assistant.chat'),
				sub: T(`assistant.inbox.action.${item.action}`) || T('assistant.inbox.actionFallback') || item.action
			};
		}
		if (item.kind === 'failedRun') {
			return {
				tile: 'failed',
				Icon: AlertTriangle,
				tag: T('assistant.inbox.tag.failed') || 'Failed',
				title: item.title || T('assistant.playbook.openButton') || 'Playbook',
				sub: item.error || T('assistant.inbox.failedRunFallback') || 'The last run failed.'
			};
		}
		const isRoutine = !!item.contextType && ROUTINE_CONTEXTS.has(item.contextType);
		return {
			tile: isRoutine ? 'routine' : 'message',
			Icon: isRoutine ? Repeat : MessageSquare,
			tag: null,
			title: item.title || T('assistant.chat'),
			sub: previewText(item.lastMessage)
		};
	};

	const renderRow = (item: InboxItem, showDot: boolean, dimmed: boolean) => {
		const { tile, Icon, tag, title, sub } = describe(item);
		// failedRun items may have no conversation (nothing was written); everything
		// conversation-related is guarded on this.
		const conversationId = item.conversationId ?? null;
		const isActive = conversationId != null && conversationId === currentConversationId;
		// Conversation rows (unread/earlier) expand to show the full message.
		// Proposals + failed-run signals have no message body — a click just opens
		// the conversation (when there is one).
		const expandable = item.kind === 'unread' || item.kind === 'earlier';
		const isOpen = expandable && conversationId != null && expanded.has(conversationId);
		const openLabel = T('assistant.inbox.openConversation') || 'Open conversation';
		const convHref =
			conversationId != null ? `#${buildPath({ route: 'chat', agentRoute: null, conversationId })}` : null;
		const openConversation = (e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			if (conversationId != null) onSelectConversation(conversationId);
		};
		const onRowActivate = () => {
			if (conversationId == null) return;
			if (expandable) toggle(conversationId);
			else onSelectConversation(conversationId);
		};
		return (
			<div
				key={`${item.kind}-${item.id}`}
				className={`ws-inbox-item ${dimmed ? 'ws-inbox-item--earlier' : ''} ${isActive ? 'active' : ''}`}
			>
				<div
					className="ws-inbox-row"
					role="button"
					tabIndex={0}
					aria-expanded={expandable ? isOpen : undefined}
					onClick={onRowActivate}
					onKeyDown={e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							onRowActivate();
						}
					}}
				>
					<span className={`ws-inbox-icon ws-inbox-icon--${tile}`}>
						<Icon style={{ width: 16, height: 16 }} />
					</span>
					<span className="ws-inbox-row-main">
						<span className="ws-inbox-row-head">
							<span className="ws-inbox-row-title">{title}</span>
							{tag && <span className={`ws-inbox-tag ws-inbox-tag--${tile}`}>{tag}</span>}
							{/* Real link to the thread — distinct from expanding the row.
							    Omitted for failed-run signals with no conversation. */}
							{convHref && (
								<a
									className="ws-inbox-link"
									href={convHref}
									onClick={openConversation}
									title={openLabel}
									aria-label={openLabel}
								>
									<MessagesSquare style={{ width: 14, height: 14 }} />
									{/* Wrapped so a narrow row can drop to the icon alone — see
									    the ws-inbox-head container query in styles/history.ts. */}
									<span className="ws-inbox-link-label">
										{T('assistant.inbox.conversation') || 'Conversation'}
									</span>
								</a>
							)}
						</span>
						{sub && !isOpen && <span className="ws-inbox-row-preview">{sub}</span>}
					</span>
					{/* Fixed-width slots so the time/dot/chevron columns line up down
					    the list and the header link always sits at the same x. */}
					<span className="ws-inbox-meta">
						{item.kind === 'unread' && conversationId != null && (
							<button
								type="button"
								className="ws-inbox-mark"
								title={T('assistant.inbox.markRead') || 'Mark as read'}
								aria-label={T('assistant.inbox.markRead') || 'Mark as read'}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									markRead(conversationId);
								}}
							>
								<Check style={{ width: 15, height: 15 }} />
							</button>
						)}
						<span className="ws-inbox-dot-slot">{showDot && <span className="ws-inbox-dot" />}</span>
						<span className="ws-inbox-when">{item.regDate ? formatWhen(item.regDate) : ''}</span>
						<span className="ws-inbox-chev-slot">
							{expandable && (
								<span className={`ws-sb-chev ${isOpen ? 'open' : ''}`}>
									<ChevronRight style={{ width: 14, height: 14 }} />
								</span>
							)}
						</span>
					</span>
				</div>
				{isOpen && (
					<div className="ws-inbox-item-body">
						<div
							className="pr-prose ws-inbox-message"
							dangerouslySetInnerHTML={{
								__html: renderMarkdown(item.lastMessage || T('assistant.inbox.noPreview'))
							}}
						/>
					</div>
				)}
			</div>
		);
	};

	const approvals = items.filter((i): i is Extract<InboxItem, { kind: 'approval' }> => i.kind === 'approval');
	const failedRuns = items.filter(i => i.kind === 'failedRun');
	const unread = items.filter(i => i.kind === 'unread');
	const earlier = items.filter(i => i.kind === 'earlier');
	const hasItems = items.length > 0;
	const EARLIER_COLLAPSED = 5;
	const visibleEarlier = earlierExpanded ? earlier : earlier.slice(0, EARLIER_COLLAPSED);
	const hiddenEarlier = earlier.length - EARLIER_COLLAPSED;

	const section = (
		labelKey: string,
		fallback: string,
		rows: InboxItem[],
		opts: { dot: boolean; dim: boolean; count: boolean; action?: React.ReactNode }
	) =>
		rows.length > 0 && (
			<div className="ws-inbox-section">
				<div className={`ws-history-group-label ${opts.action ? 'ws-inbox-group-label--action' : ''}`}>
					<span>
						{T(labelKey) || fallback}
						{opts.count && <span className="ws-inbox-section-count">{rows.length}</span>}
					</span>
					{opts.action}
				</div>
				<div className="ws-inbox-list">{rows.map(r => renderRow(r, opts.dot, opts.dim))}</div>
			</div>
		);

	return (
		<div className="ws-history">
			<div className="ws-history-inner">
				<h1 className="ws-history-title">{T('assistant.inbox') || 'Inbox'}</h1>
				{!loading && !hasItems && (
					<div className="ws-history-empty">{T('assistant.inbox.empty') || 'All caught up'}</div>
				)}
				{section('assistant.inbox.group.needsYou', 'Needs you', approvals, {
					dot: true,
					dim: false,
					count: true
				})}
				{section('assistant.inbox.group.failed', 'Playbooks that failed', failedRuns, {
					dot: true,
					dim: false,
					count: true
				})}
				{section('assistant.inbox.group.unread', 'Unread', unread, {
					dot: true,
					dim: false,
					count: true,
					action: (
						<button type="button" className="ws-inbox-markall-link" onClick={markAllRead}>
							<CheckCheck style={{ width: 14, height: 14 }} />
							{T('assistant.inbox.markAllRead') || 'Mark all as read'}
						</button>
					)
				})}
				{earlier.length > 0 && (
					<div className="ws-inbox-section ws-inbox-section--spaced">
						<div className="ws-history-group-label">{T('assistant.inbox.group.earlier') || 'Earlier'}</div>
						<div className="ws-inbox-list">{visibleEarlier.map(r => renderRow(r, false, true))}</div>
						{!earlierExpanded && hiddenEarlier > 0 && (
							<button
								type="button"
								className="ws-inbox-showmore"
								onClick={() => setEarlierExpanded(true)}
							>
								{T('assistant.inbox.showMore', { count: hiddenEarlier }) ||
									`Show ${hiddenEarlier} more`}
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default WorkspaceInbox;
